// domain/services/serviceCodeGovernance.js

import { SERVICE_CODE_ORIGIN } from '../constants/serviceCode';
import { isSameCodeString } from './codeString';
import { toAsOfDate, toDateStr } from './effectiveDating';

/**
 * The two guards that let org-authored codes share a collection with the governed catalog.
 *
 * PURE, AND NEITHER OF THEM BLOCKS ANYTHING BY ITSELF. Both return findings; what a screen does with
 * one is the screen's decision, and in the first case the decision is deliberately "advise". Keeping
 * the judgement out of here is what lets the mint form word the advice, the row menu word the
 * refusal, and a test exercise either without rendering anything.
 */

/**
 * Why a code's lifespan cannot be changed — shared by removal and retirement.
 *
 * ONE VOCABULARY FOR BOTH, because the two overlap on three of four reasons and a caller offering
 * retire beside remove reads them together. Named rather than left as bare literals: the editor maps
 * `REFERENCED` to its own wording and the use cases carry these into `ServiceCodeChangeRejected`, so
 * a typo is a silent miss — the lookup returns undefined and the reader gets the generic sentence.
 * `COVERAGE_ERRORS` next door is the same shape for the same reason.
 *
 * @domainSupport The rejection vocabulary shared by removing and retiring a code.
 * @typedef {'notFound' | 'managed' | 'referenced' | 'alreadyRemoved' | 'endsBeforeStart'} ServiceCodeChangeRejection
 */
export const SERVICE_CODE_CHANGE_REJECTION = {
  /** No such code, which is a dangling reference rather than a refusal. */
  NOT_FOUND: 'notFound',
  /** Ours, not theirs: a managed code's lifespan is an industry fact reaching every clinic at once. */
  MANAGED: 'managed',
  /** Something we can see points at it, so removing it would orphan that reference. */
  REFERENCED: 'referenced',
  /** Already tombstoned; there is nothing left to remove. */
  ALREADY_REMOVED: 'alreadyRemoved',
  /** The retirement date is before the code starts, which would close a window that never opened. */
  ENDS_BEFORE_START: 'endsBeforeStart',
};

/**
 * Managed codes already carrying the string somebody is about to mint.
 *
 * DATE-BLIND ON PURPOSE, and this is the whole design of the guard. Every other lookup in this
 * context asks its question as of a date; this one must not. A code at an industry changeover is
 * exactly the case that drives a shadow-create — the org cannot find `97153` today because ours
 * retired on 31 December, so they mint their own, and every rollup keyed on the code identity
 * quietly stops seeing them. A date-bound check would return nothing at that moment, which is the
 * one moment it has to speak. Retired and not-yet-started codes are therefore both findings.
 *
 * NOT `findByCodeOn`, which is date-bound, refuses to arbitrate between candidates and returns at
 * most one. This returns EVERY match, because the note it feeds says "we already have this" and a
 * changeover pair is two records — showing one of them would answer a question nobody asked.
 *
 * TOMBSTONES ARE EXCLUDED. A tombstoned code is a record that should never have existed, so it is
 * nothing to be shadowed by; advising an org against a code we have disowned would be advice that
 * cannot be acted on.
 *
 * MANAGED ONLY. Another org's custom code sharing the string is not a shadow and is not a finding:
 * neither is governed, nothing rolls up through either, and two orgs' `SC-DIRECT` are correctly
 * different things. The clinic's OWN code sharing the string is a different question with a
 * different answer — see `findOwnCodesMatching`, which reports a duplicate rather than a shadow.
 *
 * @param {import('../model/ServiceCode.types').ServiceCode[]} serviceCodes - any set of codes; only
 *   the managed ones are considered
 * @param {*} code - the code string as typed
 * @returns {import('../model/ServiceCode.types').ServiceCode[]} every managed match, in the order
 *   given; empty when there is nothing to advise about
 */
export function findManagedCodesMatching(serviceCodes, code) {
  return (serviceCodes ?? []).filter((serviceCode) => (
    serviceCode?.origin === SERVICE_CODE_ORIGIN.MANAGED
    && serviceCode?.isDeleted !== true
    && isSameCodeString(serviceCode?.code, code)
  ));
} // findManagedCodesMatching()

/**
 * Every code of the clinic's own already carrying this string.
 *
 * A LIST, BECAUSE MORE THAN ONE IS LEGITIMATE. A clinic can hold the same code twice over: a funder
 * that revises what a code covers reuses the string for a new period, which is two records in
 * disjoint windows — the revise-in-place shape the catalog has, for the same reason. So this
 * reports what exists and leaves the screen to tell a revision from a mistake, which is a judgement
 * about dates rather than about strings.
 *
 * ONLY THE SAME STRING ON THE SAME START DATE IS UNWRITABLE, since that is what the document id
 * collides on. A caller wanting that one question asks it of this list — `findOwnCodeOn` below.
 *
 * NOT A SHADOW, and the two findings must not be merged. A shadow collides with a code WE govern:
 * legitimate, advised against, never blocked. This is the clinic's own list, where the same string
 * in a new window is ordinary and the same string on the same day cannot be written at all.
 *
 * TOMBSTONES ARE EXCLUDED, and that is what keeps the correction path open. A removed code is not
 * something the clinic has; entering it again revives the record with the facts just typed, which is
 * how a mistyped unit basis gets fixed. Reporting it as a duplicate would close the only route to it.
 *
 * @param {import('../model/ServiceCode.types').ServiceCode[]} serviceCodes
 * @param {*} code - the code string as typed
 * @returns {import('../model/ServiceCode.types').ServiceCode[]}
 */
export function findOwnCodesMatching(serviceCodes, code) {
  return (serviceCodes ?? []).filter((serviceCode) => (
    serviceCode?.origin === SERVICE_CODE_ORIGIN.CUSTOM
    && serviceCode?.isDeleted !== true
    && isSameCodeString(serviceCode?.code, code)
  ));
} // findOwnCodesMatching()

/**
 * The clinic's own code with this string AND this start date — the one a write cannot produce.
 *
 * THE ONLY BLOCKING DUPLICATE, and deliberately no wider than the document id. Two records sharing a
 * code string may both exist, they may start on different days, and their windows MAY OVERLAP — a
 * funder's own periods are not ours to arbitrate, and refusing an overlap would refuse a state the
 * domain allows. Only the exact same code starting on the exact same day is unrepresentable, because
 * that is what `{clinic}_{code}_{validFrom}` collides on.
 *
 * Asked before the save so the user gets an explanation rather than a rejected write.
 *
 * WHAT AN OVERLAP COSTS, since it is permitted: `findByCodeOn` refuses to arbitrate between two
 * codes claiming one date, so for the days two windows share, that string stops resolving BY STRING.
 * Nothing billing-bound is affected — billing resolves by id, and the by-string lookup exists for
 * humans and migrations — but it is the reason overlaps are allowed rather than encouraged.
 *
 * @param {import('../model/ServiceCode.types').ServiceCode[]} serviceCodes
 * @param {*} code - the code string as typed
 * @param {*} validFrom - 'YYYY-MM-DD' as chosen
 * @returns {import('../model/ServiceCode.types').ServiceCode|null}
 */
export function findOwnCodeOn(serviceCodes, code, validFrom) {
  const startsOn = toAsOfDate(validFrom);

  if (startsOn === null) {
    return null;
  }
  return findOwnCodesMatching(serviceCodes, code)
    .find((serviceCode) => toDateStr(serviceCode.validFrom) === startsOn) ?? null;
} // findOwnCodeOn()

/**
 * Why this code may not be removed, or null when it may.
 *
 * REMOVAL IS A TOMBSTONE, NEVER A HARD DELETE, whatever the menu item says. The rules grant no
 * delete on this collection at all, and the entity's own invariant is that a referenced code must
 * stay resolvable — a removed code that something still points at is a permanent hold with no record
 * of what the code meant.
 *
 * "BILLED" IS NOT A SEPARATE QUESTION, AND A BILLING ITEM IS THE WRONG PLACE TO ASK IT. The stated
 * rule is "removable while never referenced or billed", but a billing item is a PROJECTION of config
 * and activities rather than a record of its own — so its absence proves nothing. Delete a code
 * because no billing item names it, and the next projection runs over the same activities and the
 * same config and produces items naming a code that no longer exists. The check would have licensed
 * exactly the orphan it was meant to prevent.
 *
 * SO CONFIG IS THE EVIDENCE, all of it: every charge master sheet the clinic has ever had —
 * superseded and tombstoned included, which is why the caller reads them all — and every treatment
 * service naming the code as its default. A code can only reach a claim through config that
 * referenced it, so config already covers everything a billing item could have told us, and covers
 * it for dates that have not been projected yet.
 *
 * THE GATE IS STILL NARROWER THAN THAT, and the remaining reason is smaller than it used to be:
 * payer coding rules, which can name a code as an override and do not exist yet. Until they do,
 * removal is allowed only in the window where NOTHING can have referenced the code — minted in this
 * editing session, sheet never saved — and every other code is retire-only.
 *
 * WIDENING THIS IS ADDITIVE, and the trigger is the override rules rather than the projector.
 * `isSessionMinted` then stops being the only way to earn a removal.
 *
 * TAKES ITS EVIDENCE AS ARGUMENTS rather than gathering it. Gathering means reading two collections,
 * which would make the rule untestable without them and would put a clock and a query behind a
 * question that is pure.
 *
 * @param {Object} subject
 * @param {import('../model/ServiceCode.types').ServiceCode|null} subject.serviceCode
 * @param {boolean} subject.isSessionMinted - minted in the editing session asking the question, and
 *   still uncommitted
 * @param {boolean} subject.hasCommittedReference - anything we CAN see points at it. Both halves:
 *   a saved charge master sheet, AND a treatment service naming it as its default. The caller
 *   supplies the first from the sheets it already holds; the use case reads the second, because no
 *   screen that offers a removal has it. A check that could not run counts as a reference.
 * @returns {ServiceCodeChangeRejection|null}
 */
export function serviceCodeRemovalRejection({
  serviceCode,
  isSessionMinted = false,
  hasCommittedReference = false,
} = {}) {
  if (serviceCode === null || serviceCode === undefined) {
    return SERVICE_CODE_CHANGE_REJECTION.NOT_FOUND;
  }
  // A managed code is not ours to remove from a browser, and the rules refuse it anyway. Named
  // rather than folded into `referenced` so a screen can say the true thing.
  if (serviceCode.origin !== SERVICE_CODE_ORIGIN.CUSTOM) {
    return SERVICE_CODE_CHANGE_REJECTION.MANAGED;
  }
  if (serviceCode.isDeleted === true) {
    return SERVICE_CODE_CHANGE_REJECTION.ALREADY_REMOVED;
  }
  if (hasCommittedReference === true || isSessionMinted !== true) {
    return SERVICE_CODE_CHANGE_REJECTION.REFERENCED;
  }
  return null;
} // serviceCodeRemovalRejection()

/**
 * Why this code may not be retired on this date, or null when it may.
 *
 * RETIREMENT IS ALWAYS AVAILABLE ON AN ORG'S OWN CODE, referenced or not — that is the point of it.
 * A code that has been billed cannot be removed and must still be closable when its funder stops
 * accepting it, and closing a window changes nothing already resolved through it: `retiredOn` is the
 * LAST billable service date, so past dates keep resolving exactly as they did.
 *
 * A MANAGED CODE IS REFUSED. Its lifespan is an industry fact that reaches every clinic at once, and
 * one clinic retiring it for everybody is not a capability this collection has.
 *
 * AN INVERTED WINDOW IS REFUSED, and it is the one rejection here that is about the DATE rather than
 * about the record. A `retiredOn` before `validFrom` closes a window that never opened:
 * `isBillableOn` asks whether the service date falls inside it and no date does, so the code becomes
 * billable on NO date at all. Nothing throws and nothing looks wrong — the code simply stops
 * resolving, which reads as configuration somebody forgot rather than as a date typed backwards.
 *
 * A BACKDATED RETIREMENT IS STILL FINE. An org told in March that a code stopped being accepted in
 * January is recording a fact; only a date before the code STARTED is impossible.
 *
 * @param {import('../model/ServiceCode.types').ServiceCode|null} serviceCode
 * @param {*} [retiredOn] - 'YYYY-MM-DD' the proposed last billable date; null or omitted reopens the
 *   code, which no window can contradict
 * @returns {ServiceCodeChangeRejection|null}
 */
export function serviceCodeRetirementRejection(serviceCode, retiredOn = null) {
  if (serviceCode === null || serviceCode === undefined) {
    return SERVICE_CODE_CHANGE_REJECTION.NOT_FOUND;
  }
  if (serviceCode.origin !== SERVICE_CODE_ORIGIN.CUSTOM) {
    return SERVICE_CODE_CHANGE_REJECTION.MANAGED;
  }
  if (serviceCode.isDeleted === true) {
    return SERVICE_CODE_CHANGE_REJECTION.ALREADY_REMOVED;
  }
  const closesOn = toAsOfDate(retiredOn);
  const startsOn = toDateStr(serviceCode.validFrom);

  // Only compared when BOTH read. An unreadable retirement is the use case's TypeError, and a code
  // whose own start cannot be read is already excluded from every date by resolution.
  if (closesOn !== null && startsOn !== null && closesOn < startsOn) {
    return SERVICE_CODE_CHANGE_REJECTION.ENDS_BEFORE_START;
  }
  return null;
} // serviceCodeRetirementRejection()
